import * as React from 'react'
import {Dialog, DialogBody, DialogHeading} from '@frontrow/console/src/components/ui/dialog'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faPencilAlt} from '@fortawesome/free-solid-svg-icons'
import {Button} from '@frontrow/console/src/components/button'
import {useSafeCallbackExtraDeps, unCallbackFn} from '@freckle/react-hooks'
import isEqual from 'lodash/isEqual'

// Components
import {CrmManagedAlert} from '@frontrow/console/src/components/common/crm-managed-alert'
import DistrictForm from '@frontrow/console/src/components/pages/districts-page/district-form'
// Models
import {type DistrictT, type BasicDistrictT} from '@frontrow/console/src/models/district'
import {useModalForm} from '@frontrow/console/src/hooks/use-modal-form'

/** @internal */
export type Props = {
  updateDistrict: (
    districtId: number,
    updateDistrictData: BasicDistrictT,
    clearClassLinkOneRosterToken: boolean
  ) => Promise<unknown>
  districtToUpdate: DistrictT
  deleteCleverAndClasslinkAssociations: (districtId: number) => void
}

export default function DistrictUpdateButtonModal(props: Props): React.ReactElement {
  const {isOpen, open, close} = useModalForm()
  const {districtToUpdate} = props
  const {active, crmId} = districtToUpdate

  const submitForm = useSafeCallbackExtraDeps<
    (
      updatedDistrictData: BasicDistrictT,
      clearClassLinkOneRosterToken: boolean,
      shouldDeleteCleverAndClasslinkAssociations?: boolean
    ) => void,
    {
      updateDistrict: (
        districtId: number,
        updateDistrictData: BasicDistrictT,
        clearClassLinkOneRosterToken: boolean
      ) => Promise<unknown>
      districtToUpdate: DistrictT
      deleteCleverAndClasslinkAssociations: (districtId: number) => void
      close: () => void
    }
  >(
    ({updateDistrict, districtToUpdate, deleteCleverAndClasslinkAssociations, close}) =>
      (
        updatedDistrictData: BasicDistrictT,
        clearClassLinkOneRosterToken: boolean,
        shouldDeleteCleverAndClasslinkAssociations?: boolean
      ) => {
        updateDistrict(districtToUpdate.id, updatedDistrictData, clearClassLinkOneRosterToken).then(
          () => {
            if (shouldDeleteCleverAndClasslinkAssociations) {
              deleteCleverAndClasslinkAssociations(districtToUpdate.id)
            }
          }
        )
        close()
      },
    [],
    {
      updateDistrict: {value: props.updateDistrict, comparator: () => true},
      districtToUpdate: {value: props.districtToUpdate, comparator: isEqual},
      deleteCleverAndClasslinkAssociations: {
        value: props.deleteCleverAndClasslinkAssociations,
        comparator: () => true
      },
      close: {value: close, comparator: () => true}
    }
  )

  return (
    <React.Fragment>
      <Button title="Update District" style="link" onClick={open}>
        <FontAwesomeIcon icon={faPencilAlt} size="lg" />
      </Button>
      <Dialog open={isOpen} onClose={close}>
        <DialogHeading>Update {districtToUpdate.name}</DialogHeading>
        <CrmManagedAlert
          active={active}
          crmId={crmId}
          crmManagedEntity="district"
          instructions="To update name and address information, make the changes in CRM."
        />
        <DialogBody>
          <DistrictForm
            districtToUpdate={districtToUpdate}
            onSubmit={unCallbackFn(submitForm)}
            onCancel={close}
            isCreate={false}
          />
        </DialogBody>
      </Dialog>
    </React.Fragment>
  )
}
